import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Row,
  Col,
  Alert
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import { Editor } from '@tinymce/tinymce-react';

const PortfolioAdd = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const navigate = useNavigate();
  const toast = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [portfolioID, setPortfolioID] = useState(null);

  const [formData, setFormData] = useState({
    office: "coders",
    category: "website",
    title: "",
    description: "",
    image: null,
    meta_title: "",
    meta_description: "",
    meta_keywords: ""
  });
  const [imagePreview, setImagePreview] = useState(null);

  const breadcrumbItems = [
    { title: "Content", link: "/" },
    { title: "Portfolios", link: "/admin/office/website/portfolios" },
    { title: editId ? "Edit Portfolio" : "Add Portfolio", link: "#!" },
  ];

  const officeOptions = [
    { value: 'coders', label: 'Coders' },
    { value: 'creatives', label: 'Creatives' }
  ];



  const fetchPortfolioData = React.useCallback(async () => {
    setLoadingPortfolio(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/company-portfolio/${editId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });

      const portfolioData = response.data.data;

      setPortfolioID(portfolioData.id);

      if (portfolioData) {
        setFormData({
          office: portfolioData.office || "coders",
          category: portfolioData.category || "website",
          title: portfolioData.title || "",
          description: portfolioData.description || "",
          image: null,
          meta_title: portfolioData.meta_title || "",
          meta_description: portfolioData.meta_description || "",
          meta_keywords: portfolioData.meta_keywords || ""
        });

        if (portfolioData.image) {
          setImagePreview(portfolioData.image);
        }
      }
    } catch (error) {
      setFetchError("Failed to load portfolio data");
    } finally {
      setLoadingPortfolio(false);
    }
  }, [editId]);

  useEffect(() => {
    if (editId) fetchPortfolioData();
  }, [editId, fetchPortfolioData]);


  // Custom Upload Adapter for Base64


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, description: content }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Explicitly append fields based on backend requirements
      formDataToSend.append('office', formData.office);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('meta_title', formData.meta_title);
      formDataToSend.append('meta_description', formData.meta_description);
      formDataToSend.append('meta_keywords', formData.meta_keywords);

      if (formData.image instanceof File) {
        formDataToSend.append('image', formData.image);
      }

      const url = editId
        ? `${API_BASE_URL}/company-portfolio/update/${portfolioID}`
        : `${API_BASE_URL}/company-portfolio/add`;

      await axios.post(url, formDataToSend, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        }
      });

      showToast('success', 'Success', editId ? 'Updated successfully' : 'Created successfully');
      setTimeout(() => navigate('/admin/office/website/portfolios'), 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Operation failed";
      showToast('error', 'Error', errorMsg);
      if (error.response?.status === 422) {
        console.log("Validation Details:", error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingPortfolio) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Edit Portfolio" breadcrumbItems={breadcrumbItems} />
          <Card><CardBody className="text-center py-5"><Spinner color="primary" /></CardBody></Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={editId ? "Edit Portfolio" : "Add Portfolio"} breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">{editId ? 'Edit Portfolio' : 'Create New Portfolio'}</h4>
            </CardHeader>
            <CardBody>
              {fetchError && <Alert color="danger">{fetchError}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <FormGroup>
                      <Label for="title">Title <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter portfolio title"
                        required
                      />
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="office">Office</Label>
                          <Input type="select" name="office" value={formData.office} onChange={handleInputChange} className="form-select">
                            {officeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="category">Category</Label>
                          <Input
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            placeholder="e.g. website"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Col>

                  <Col lg={4}>
                    <Card className="shadow-sm">
                      <CardBody>
                        <FormGroup>
                          <Label for="image">Featured Image</Label>
                          <Input type="file" onChange={handleImageChange} accept="image/*" />
                          {imagePreview && (
                            <div className="mt-2 text-center">
                              <img src={imagePreview} alt="Preview" className="img-thumbnail" style={{ maxHeight: "150px" }} />
                            </div>
                          )}
                        </FormGroup>
                      </CardBody>
                    </Card>


                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col lg={12}>
                    <FormGroup>
                      <Label for="description">Content / Description <span className="text-danger">*</span></Label>
                      <div className="border rounded">
                        <Editor
                          apiKey="i62vjev79t07dt2xrxeki5yrip8smtvo5fmnhqip86ekvg76"
                          value={formData.description}
                          init={{
                            height: 500,
                            menubar: true,
                            plugins: [
                              'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                              'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                              'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | ' +
                              'bold italic forecolor | alignleft aligncenter ' +
                              'alignright alignjustify | bullist numlist outdent indent | ' +
                              'removeformat | help',
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                            images_upload_handler: (blobInfo, progress) => new Promise((resolve, reject) => {
                              const reader = new FileReader();
                              reader.readAsDataURL(blobInfo.blob());
                              reader.onload = () => {
                                resolve(reader.result);
                              };
                              reader.onerror = error => reject(error);
                            })
                          }}
                          onEditorChange={(content, editor) => {
                            handleContentChange(content);
                          }}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col lg={12}>
                    <Card className="shadow-sm">
                      <CardHeader><h5 className="mb-0">SEO Settings</h5></CardHeader>
                      <CardBody>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="meta_title">Meta Title</Label>
                              <Input name="meta_title" value={formData.meta_title} onChange={handleInputChange} />
                            </FormGroup>
                            <FormGroup>
                              <Label for="meta_keywords">Meta Keywords</Label>
                              <Input name="meta_keywords" value={formData.meta_keywords} onChange={handleInputChange} required />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="meta_description">Meta Description</Label>
                              <Input type="textarea" name="meta_description" value={formData.meta_description} onChange={handleInputChange} rows={5} />
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <div className="mt-4 d-flex justify-content-between">
                  <Button type="button" color="light" onClick={() => navigate(-1)}>Cancel</Button>
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    {editId ? 'Update Portfolio' : 'Save Portfolio'}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
      <Toast ref={toast} position="top-right" />
    </React.Fragment>
  );
};

export default PortfolioAdd;